/**
 * Web push (Firebase Cloud Messaging) — browser side.
 *
 * Only runs in a real browser; inside the Expo WebView the native shell owns FCM
 * (see AuthContext → postToShell "auth.login"). `setupWebPush()` is therefore gated by
 * `!isInWebView()` at the call site (components/PushNotifications.tsx).
 *
 * Flow: feature-detect → ensure config → ask permission → register the SW →
 * get the FCM token → POST it to /api/v1/me/devices (registerDevice). Foreground
 * messages are surfaced via the `onForeground` callback. On sign-out,
 * `teardownWebPush()` unregisters the device and deletes the token, so the next
 * person on this browser doesn't get the previous user's pushes.
 *
 * Config comes from VITE_FIREBASE_* env vars (read at BUILD time). The VAPID key
 * (Firebase console → Project settings → Cloud Messaging → Web Push certificates)
 * is optional: without it the SDK uses Firebase's default key pair.
 */

import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
} from "firebase/messaging";

import { registerDevice, revokeDevice } from "../api/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const VAPID_KEY: string | undefined = import.meta.env.VITE_FIREBASE_VAPID_KEY || undefined;
const APP_VERSION = "1.0.0";

// The /me/devices row this tab registered — revoked on sign-out (teardownWebPush).
let registeredDeviceId: string | null = null;

/** True once the env vars are filled with real (non-placeholder) values. */
function isConfigured(): boolean {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId &&
      !firebaseConfig.apiKey.startsWith("REPLACE_"),
  );
}

function app(): FirebaseApp {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

/**
 * Register the FCM service worker, passing the config as query params so the worker
 * (which can't read import.meta.env) can initializeApp itself.
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey ?? "",
    authDomain: firebaseConfig.authDomain ?? "",
    projectId: firebaseConfig.projectId ?? "",
    messagingSenderId: firebaseConfig.messagingSenderId ?? "",
    appId: firebaseConfig.appId ?? "",
    measurementId: firebaseConfig.measurementId ?? "",
  });
  const registration = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${params.toString()}`,
    { scope: "/" },
  );
  // register() resolves while a first-time worker is still installing; subscribing to push
  // before it is active fails ("Subscription failed - no active Service Worker").
  await untilActive(registration);
  return registration;
}

/** Resolves once the registration has an active worker (10 s at most). */
function untilActive(registration: ServiceWorkerRegistration): Promise<void> {
  const worker = registration.installing ?? registration.waiting;
  if (registration.active || !worker) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Service worker did not activate in 10 s")), 10_000);
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") {
        clearTimeout(timer);
        resolve();
      } else if (worker.state === "redundant") {
        clearTimeout(timer);
        reject(new Error("Service worker install failed"));
      }
    });
  });
}

export interface WebPushOptions {
  /** Called when a push arrives while the app is in the foreground. */
  onForeground?: (payload: MessagePayload) => void;
  /** The signed-in user: the device is registered once per user + token, not on every load. */
  userId?: string;
}

// The last registration from this browser. Re-registering the same token for the same user on
// every page load cost a write request each time; it is renewed after REREGISTER_MS instead
// (keeps last_seen fresh and re-adds a device the server dropped).
const REGISTERED_KEY = "emx.push.registered";
const REREGISTER_MS = 12 * 60 * 60 * 1000;

interface Registered {
  userId: string;
  token: string;
  deviceId: string;
  at: number;
}

function readRegistered(): Registered | null {
  try {
    return JSON.parse(localStorage.getItem(REGISTERED_KEY) ?? "null") as Registered | null;
  } catch {
    return null;
  }
}

function writeRegistered(value: Registered | null): void {
  try {
    if (value) localStorage.setItem(REGISTERED_KEY, JSON.stringify(value));
    else localStorage.removeItem(REGISTERED_KEY);
  } catch {
    /* storage blocked: the next load registers again */
  }
}

/**
 * Idempotent: requests permission, obtains the FCM token, registers the device, and
 * wires the foreground listener. Returns an unsubscribe fn for the foreground listener
 * (no-op when push is unavailable/declined). Safe to call on every auth change.
 */
export async function setupWebPush(opts: WebPushOptions = {}): Promise<() => void> {
  const noop = () => {};

  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return noop;

  if (!isConfigured()) {
    console.warn(
      "[push] Firebase web push not configured — fill the VITE_FIREBASE_* vars in .env to enable it.",
    );
    return noop;
  }

  if (!(await isSupported())) {
    console.info("[push] This browser does not support Firebase Cloud Messaging.");
    return noop;
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    console.info("[push] Notification permission not granted:", permission);
    return noop;
  }

  try {
    const swReg = await registerServiceWorker();
    const messaging = getMessaging(app());

    // NOTE: getToken() is flagged @deprecated in firebase 12 (editor-only hint — it does
    // not fail tsc/build) in favour of the FID-based register()/onRegistered() flow. We
    // deliberately keep it: it returns a classic FCM *registration token*, which is exactly
    // what /api/v1/me/devices (registerDevice → fcm_token) and the native Expo shells expect.
    // The FID flow would target by Firebase Installation ID, requiring a backend send change.
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) {
      console.warn("[push] No FCM token returned.");
      return noop;
    }

    const known = readRegistered();
    if (opts.userId && known?.userId === opts.userId && known.token === token && Date.now() - known.at < REREGISTER_MS) {
      registeredDeviceId = known.deviceId; // already registered: no write on every page load
    } else {
      const device = await registerDevice({
        platform: "web",
        fcm_token: token,
        app_version: APP_VERSION,
        device_label: navigator.userAgent.slice(0, 120),
      });
      registeredDeviceId = device.id;
      if (opts.userId) writeRegistered({ userId: opts.userId, token, deviceId: device.id, at: Date.now() });
    }

    setSetupError(null);
    return onMessage(messaging, (payload) => opts.onForeground?.(payload));
  } catch (err) {
    console.error("[push] Failed to set up web push:", err);
    setSetupError(describeSetupError(err));
    return noop;
  }
}

// Why this browser's last push setup failed (null: it worked, or hasn't run), so the profile can
// say "couldn't connect" instead of "On" when the permission is granted but registration failed.
let setupError: string | null = null;

/** Fired whenever pushSetupError() changes. */
export const PUSH_STATUS_EVENT = "emx:push-status";

export function pushSetupError(): string | null {
  return setupError;
}

function setSetupError(message: string | null): void {
  if (message === setupError) return;
  setupError = message;
  window.dispatchEvent(new Event(PUSH_STATUS_EVENT));
}

function describeSetupError(err: unknown): string {
  if ((err as { code?: string }).code === "messaging/token-subscribe-failed") {
    // Seen as FCM 401 "missing required authentication credential" in a browser profile where
    // the same site works in an incognito window — i.e. something in that profile (typically an
    // extension that rewrites request headers) strips Firebase's auth header.
    return (
      "Google's notification service refused this browser's registration. This usually comes " +
      "from a browser extension that changes web requests (the same site works in an incognito " +
      "window). Turn such extensions off for this site, then try again."
    );
  }
  return "This browser couldn't be registered for notifications. Check your connection, then try again.";
}

/** This browser's notification state, for the profile's notification controls. */
export type PushPermission = NotificationPermission | "unsupported" | "unconfigured";

export async function pushPermission(): Promise<PushPermission> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("Notification" in window)) {
    return "unsupported";
  }
  if (!isConfigured()) return "unconfigured";
  if (!(await isSupported())) return "unsupported";
  return Notification.permission;
}

/** Fired to make components/PushNotifications set web push up again (permission just granted). */
export const PUSH_RETRY_EVENT = "emx:push-retry";

// The user's on/off choice for notifications in this browser (profile panel). Per browser, not
// per account: the account-wide switch is the profile's `push_master_kill`.
const THIS_BROWSER_KEY = "emx.push.thisBrowser";

/** Whether notifications are switched on for this browser (on unless switched off here). */
export function pushOnHere(): boolean {
  try {
    return localStorage.getItem(THIS_BROWSER_KEY) !== "off";
  } catch {
    return true;
  }
}

/**
 * Switch notifications for this browser. Off: unregister this browser and delete its token, and
 * stop setting push up on later visits. On: ask for permission if the browser still allows asking
 * (once the site is blocked, only the site settings can allow it — watchPushPermission notices
 * that), then set push up again. Returns the browser's permission afterwards.
 */
export async function setPushOnHere(on: boolean): Promise<NotificationPermission> {
  try {
    localStorage.setItem(THIS_BROWSER_KEY, on ? "on" : "off");
  } catch {
    /* storage blocked: applies to this visit only */
  }
  if (!on) {
    await teardownWebPush();
    window.dispatchEvent(new Event(PUSH_RETRY_EVENT)); // drops the foreground listener
    return Notification.permission;
  }
  const permission =
    Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
  if (permission === "granted") window.dispatchEvent(new Event(PUSH_RETRY_EVENT));
  return permission;
}

/**
 * Calls back when the notification permission changes outside the page — e.g. the user unblocks
 * the site in the browser's site settings. Returns the unsubscribe function.
 */
export function watchPushPermission(onChange: (permission: NotificationPermission) => void): () => void {
  let status: PermissionStatus | null = null;
  let stopped = false;
  navigator.permissions
    ?.query({ name: "notifications" })
    .then((s) => {
      if (stopped) return;
      status = s;
      s.onchange = () => onChange(Notification.permission);
    })
    .catch(() => {
      /* Permissions API unavailable: the profile's "Check again" still works */
    });
  return () => {
    stopped = true;
    if (status) status.onchange = null;
  };
}

/**
 * Sign-out (ACCT-19): revoke this browser's device row (while the session is still valid),
 * then delete the FCM token, so a shared browser gets no more of the signed-out user's alerts. Deleting the token also covers an expired session,
 * where the revoke call can't authenticate: FCM then reports the old token as
 * unregistered and the backend revokes it on the next send. Never throws.
 */
export async function teardownWebPush(): Promise<void> {
  const deviceId = registeredDeviceId ?? readRegistered()?.deviceId ?? null;
  registeredDeviceId = null;
  writeRegistered(null);
  setSetupError(null);
  if (deviceId) {
    try {
      await revokeDevice(deviceId);
    } catch {
      /* expired session / offline — the token deletion below still stops delivery */
    }
  }
  if (!isConfigured() || !("serviceWorker" in navigator)) return;
  try {
    if (!(await isSupported()) || Notification.permission !== "granted") return;
    // Deprecated in firebase 12 alongside getToken() (see the note there); it is the
    // matching call for the classic registration token we registered.
    await deleteToken(getMessaging(app()));
  } catch (err) {
    console.warn("[push] Failed to delete the FCM token:", err);
  }
}
